<template>
  <a-popover
    placement="bottomLeft"
    :overlayClassName="'right-popover'"
    v-model:visible="popoverVisible"
  >
    <slot>
      <div class="avatar">
        <Avatar
          size="32"
          class="editImg"
          :url="previewFile(userInfo?.avatar)"
          :name="userInfo?.name"
        >
        </Avatar>
      </div>
    </slot>
    <template #content>
      <div class="content" ref="dropdown">
        <div class="item">
          <div class="item-avatar">
            <Avatar
              size="50"
              class="editImg"
              :url="previewFile(currentOrganization?.logo)"
              :name="currentOrganization?.organizationName"
            >
            </Avatar>
          </div>
          <div class="item-first h-full flex flex-col justify-between items-start">
            <span class="text-lg">{{ currentOrganization?.organizationName }}</span>
            <div class="flex items-center justify-between" style="margin: 0; margin-top: 6px">
              <span class="free" :style="getAccountTypeStyle(currentOrganization)">{{
                getAccountTypeDesc(currentOrganization)
              }}</span>
              <span
                class="ml-2"
                style="font-weight: normal; color: rgba(0, 0, 0, 0.5)"
                v-if="
                  [SeatVersion.Pro, SeatVersion.CoCreation].includes(
                    currentOrganization?.seatVersion,
                  ) && currentOrganization?.validityPeriodEnd
                "
              >
                {{ currentOrganization.validityPeriodEnd?.split(' ').at(0) }}到期
              </span>
            </div>
          </div>
        </div>
        <a-divider />

        <a-popover
          placement="rightTop"
          trigger="hover"
          :overlayClassName="'avatar-popover'"
          :getPopupContainer="getOrgPopoverContainer"
          v-model:visible="organizationPopoverVisible"
        >
          <div class="item" style="width: 253px">
            <div class="item-avatar">
              <Icon code="xe67d;" style="margin-right: 12px" :size="16"> </Icon>
            </div>
            <div class="item-content">切换团队</div>
          </div>
          <template #content>
            <div style="flex: 1; overflow-y: auto;">
              <div
                class="item"
                :class="{ current: organizationId === o.id }"
                v-for="o in organization"
                :key="o.id"
                @click="goChangeOrganization(o.id)"
              >
                <div class="item-avatar">
                  <Avatar
                    size="40"
                    class="editImg"
                    :name="o.organizationName"
                    :url="previewFile(o.logo)"
                  >
                  </Avatar>
                </div>
                <div class="item-content">
                  <div class="content-one">{{ o.organizationName }}</div>
                  <span class="free" :style="getAccountTypeStyle(o)">{{
                    getAccountTypeDesc(o)
                  }}</span>
                </div>
                <div class="current-mark" v-if="organizationId === o.id">
                  <CheckOutlined />
                </div>
              </div>
            </div>
            <a-divider />
            <div class="item-add flex-shrink-0" @click="openCreateModal">
              <div class="item-avatar">
                <Icon code="xe891;" :size="15" color="#268feb"> </Icon>
              </div>
              <div class="create-content">创建团队</div>
            </div>
          </template>
        </a-popover>

        <div class="item" @click="goHome">
          <div class="item-avatar">
            <Icon code="xe609;" style="margin-right: 12px" :size="16"> </Icon>
          </div>
          <div class="item-content">官网首页</div>
        </div>

        <a-divider v-if="canManage" />
        <a-tooltip
          v-if="canManage"
          title="个人空间不提供成员邀请功能。您可以选择创建团队账户，随后邀请同事加入，以便团队协作。"
          placement="topLeft"
          :visible="inviteTooltipVisible && isPersonalOrg"
          @visibleChange="(v: boolean) => (inviteTooltipVisible = v)"
        >
          <div
            class="item"
            :class="{ disabled: userInfo?.organizationType === 'personal' }"
            @click="handleInviteVisible(true)"
          >
            <div class="item-avatar">
              <Icon code="xe643;" style="margin-right: 12px" :size="16"> </Icon>
            </div>
            <div class="item-content">邀请成员</div>
          </div>
        </a-tooltip>
        <a-divider v-if="canManage" />
        <div
          v-if="canManage"
          class="item"
          :class="{ disabled: userInfo?.organizationType === 'personal' }"
          @click="goMemberSettings"
        >
          <div class="item-avatar">
            <Icon code="xe7ea;" style="margin-right: 12px" :size="16"> </Icon>
          </div>
          <div class="item-content">成员列表</div>
        </div>
        <div v-if="canManage" class="item" @click="goSeatSettings">
          <div class="item-avatar">
            <Icon code="xe7ea;" style="margin-right: 12px" :size="16"> </Icon>
          </div>
          <div class="item-content">席位设置</div>
        </div>
        <a-tooltip
          v-if="canManage"
          placement="topLeft"
          :visible="enterpriseSettingTooltipVisible && isPersonalOrg"
          @visibleChange="(v: boolean) => (enterpriseSettingTooltipVisible = v)"
        >
          <div
            class="item"
            :class="{ disabled: userInfo?.organizationType === 'personal' }"
            @click="goEnterpriseSettings"
          >
            <div class="item-avatar">
              <Icon code="xe634;" style="margin-right: 12px" :size="16"> </Icon>
            </div>
            <div class="item-content">团队设置</div>
          </div>
        </a-tooltip>
        <a-divider v-if="!isOrgOwner" />
        <div v-if="!isOrgOwner" class="item" @click="openLeaveTeamModal">
          <div class="item-avatar">
            <Icon code="xe892;" style="margin-right: 12px" :size="16"> </Icon>
          </div>
          <div class="item-content">退出团队</div>
        </div>
      </div>
    </template>
  </a-popover>
  <InviteModal
    :visible="inviteVisible"
    @close="handleInviteVisible(false)"
    :inviteToken="inviteToken"
  />
  <Modal v-model:visible="createVisible" title="创建团队" @ok="handleOkCreate">
    <a-form
      labelAlign="left"
      ref="createFormRef"
      :labelCol="{ span: 4 }"
      :wrapperCol="{ span: 20 }"
      :model="createForm"
    >
      <BasicFormItem
        v-for="item in createDefs"
        :key="item.key"
        :item="item"
        v-model:value="createForm[item.key]"
      />
    </a-form>
  </Modal>
  <StatusModal v-model:visible="creatingModalVisible" text="正在创建账户..." />
  <SettingModal :initTab="settingType" v-model:visible="settingVisible"></SettingModal>
  <Modal title="退出团队" v-model:visible="transferVisible" @ok="transferOwner" @open="handleOpen">
    <div>
      您确定要退出「{{
        currentOrganization?.organizationName
      }}」吗？请注意，退出后您将无法再访问团队的任何内容，且此操作无法撤销。为避免数据丢失，请及时将您的项目和空间移交给其他成员。感谢您以往的贡献！
    </div>
    <div style="margin-top: 8px">
      选择移交对象<a-select
        style="margin-left: 8px; width: 140px"
        size="small"
        placeholder="请选择移交对象"
        :fieldNames="{ value: 'id', label: 'employeeName' }"
        :options="employeeList"
        v-model:value="leaveReceiverId"
      ></a-select>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import { CheckOutlined } from '@ant-design/icons-vue'
import { Icon } from '@elecmap/icon'
import { message } from 'ant-design-vue'
import { Avatar, Modal, BasicFormItem } from '@elecmap/elecmap-ui'
import { exitOrganization, listEmployeeByOrganizationId } from '@elecmap/api/src/ram'
import { useOrganization } from '@/store/organization'
import { useSpaceStore } from '@/store/space'
import InviteModal from '@elecmap/shared/src/components/InviteModal.vue'
import { previewFile } from '@elecmap/api/src/cad/file'
import SettingModal from '@/views/setting/setting-modal.vue'
import { SeatVersion } from '@elecmap/api/src/ram/seat.d'
import StatusModal from '@elecmap/shared/src/components/StatusModal.vue'
import { getHttpErrorMessage } from '@/utils/http-error-message'
import { useOrganizationDropdown } from './use-organization-dropdown'

const {
  dropdown,
  popoverVisible,
  organizationPopoverVisible,
  userInfo,
  isPersonalOrg,
  canManage,
  organization,
  organizationId,
  currentOrganization,
  getAccountTypeDesc,
  getAccountTypeStyle,
  goChangeOrganization,
  inviteVisible,
  inviteToken,
  handleInviteVisible,
  inviteTooltipVisible,
  goHome,
  settingVisible,
  settingType,
  goEnterpriseSettings,
  goSeatSettings,
  goMemberSettings,
  createFormRef,
  createVisible,
  creatingModalVisible,
  createForm,
  createDefs,
  openCreateModal,
  handleOkCreate,
  store,
} = useOrganizationDropdown()

const isOrgOwner = computed(() => {
  const uid = userInfo.value?.userId
  const ownerId = currentOrganization.value?.owner
  if (uid == null || ownerId == null) return false
  return Number(uid) === Number(ownerId)
})

function getOrgPopoverContainer() {
  return dropdown.value as HTMLElement
}

const enterpriseSettingTooltipVisible = ref(false)
const transferVisible = ref(false)
const employeeList = ref<any[]>([])
const leaveReceiverId = ref<number>()

async function transferOwner() {
  if (!leaveReceiverId.value) return
  try {
    const data = await exitOrganization(
      { receiverEmployeeId: leaveReceiverId.value },
      { hideToast: true },
    )
    useOrganization().setCurrentOrganizationId(data)
    const ss = useSpaceStore()
    ss.navs = []
    ss.currentSpace = undefined
    ss.orgSpaceList = []
    if (data) {
      window.location.href = '/#/workbench'
      nextTick(() => {
        window.location.reload()
      })
    } else {
      createVisible.value = true
    }
    store.setOrganization(store.organization.filter(o => o.id !== store.currentOrganization!.id))
    transferVisible.value = false
  } catch (e) {
    message.error(getHttpErrorMessage(e, '退出团队失败'))
  }
}

function openLeaveTeamModal() {
  popoverVisible.value = false
  transferVisible.value = true
}

function handleOpen() {
  listEmployeeByOrganizationId({
    organizationId: currentOrganization.value!.id,
  }).then(data => {
    employeeList.value = data.filter(d => d.userId !== userInfo.value!.userId)
  })
}

defineExpose({
  openCreateModal,
})
</script>

<style lang="less" scoped>
.avatar {
  width: 32px;
  height: 32px;
  background-color: #f0f0f0;
  overflow: hidden;
  cursor: pointer;
  user-select: none;
}
</style>

<style lang="less">
.ant-popover-inner-content {
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
</style>
